import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTrainingPlan, getTrainingWeeks, getWeeklySessions, completeWeeklySession, updateGoalsOnSessionComplete } from '../api/athlete';
import { useAuth } from '../contexts/AuthContext';
import { TrainingPlan, TrainingWeek, WeeklySession, SessionType } from '../types/athlete';
import SessionCompletionModal from './sessions/SessionCompletionModal';
import { ArrowLeft, Calendar, Loader, Droplets, Bike, PersonStanding, Dumbbell, CheckCircle, Clock } from 'lucide-react';
import { formatRaceDateWithCountdown, calculateRaceProgress, getProximityIcon } from '../lib/utils/dateUtils';

const TrainingPlanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [weeks, setWeeks] = useState<TrainingWeek[]>([]);
  const [sessions, setSessions] = useState<Record<string, WeeklySession[]>>({});
  const [loading, setLoading] = useState(true);
  const [completingSession, setCompletingSession] = useState<WeeklySession | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!id || !user) return;
    let isMounted = true;
    const loadPlanData = async () => {
      try {
        const planData = await getTrainingPlan(user.id, id);
        if (isMounted) setPlan(planData);

        const weeksData = await getTrainingWeeks(user.id, id);
        if (isMounted) setWeeks(weeksData);

        const sessionsByWeek: Record<string, WeeklySession[]> = {};
        for (const week of weeksData) {
          const weekSessions = await getWeeklySessions(user.id, week.id);
          sessionsByWeek[week.id] = weekSessions;
        }
        if (isMounted) setSessions(sessionsByWeek);
      } catch (error) {
        console.error('Error loading plan:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadPlanData();
    return () => { isMounted = false; };
  }, [id, user]);

  const getDisciplineIcon = (discipline: string) => {
    switch (discipline) {
      case 'swim': return <Droplets className="text-blue-400" size={16} />;
      case 'bike': return <Bike className="text-green-400" size={16} />;
      case 'run': return <PersonStanding className="text-orange-400" size={16} />;
      case 'strength': return <Dumbbell className="text-purple-400" size={16} />;
      default: return <Dumbbell size={16} />;
    }
  };

  const handleCompleteSession = async (data: {
    completed: boolean;
    actual_duration_minutes?: number;
    felt_rating?: number;
    completion_notes?: string;
  }) => {
    if (!completingSession) return;

    try {
      const updatedSession = await completeWeeklySession(completingSession.id, data);

      const weekId = completingSession.week_id;
      setSessions(prev => ({
        ...prev,
        [weekId]: prev[weekId].map(s =>
          s.id === completingSession.id ? updatedSession : s
        ),
      }));

      if (data.completed) {
        await updateGoalsOnSessionComplete(completingSession, true);
      }
    } catch (error) {
      console.error('Error completing session:', error);
    } finally {
      setCompletingSession(null);
    }
  };

  const allSessions = Object.values(sessions).flat();
  const completedCount = allSessions.filter(s => s.completed).length;
  const totalCount = allSessions.length;
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader className="animate-spin" /></div>;
  }

  if (!plan) {
    return <div className="text-center py-12">Plan not found</div>;
  }

  return (
    <div>
      <Link
        to="/plans"
        className="flex items-center space-x-2 text-muted hover:text-[#3b82f6] mb-6 transition-colors"
      >
        <ArrowLeft size={18} />
        <span>Back to Plans</span>
      </Link>

      <div className="card-base p-8 mb-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">{plan.name}</h1>
            <p className="text-muted mb-4">{plan.description || 'No description'}</p>

            {/* Race Date Countdown */}
            {(() => {
              const raceInfo = formatRaceDateWithCountdown(plan.end_date);
              const icon = getProximityIcon(raceInfo.proximityStatus);
              const progress = calculateRaceProgress(plan.start_date, plan.end_date);

              return (
                <div className="mt-4 p-4 bg-[#1a1f2e] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl">{icon}</span>
                      <div>
                        <p className="text-sm text-muted">Race Date</p>
                        <p className="font-semibold text-white">{raceInfo.formatted}</p>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-full ${raceInfo.colorClass} font-semibold`}>
                      {raceInfo.displayText}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-muted mb-1">
                      <span>Training Progress</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="h-2 bg-[#141921] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          raceInfo.proximityStatus === 'race_week' ? 'bg-red-500' :
                          raceInfo.proximityStatus === 'imminent' ? 'bg-orange-500' :
                          raceInfo.proximityStatus === 'approaching' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          <span className={`px-3 py-1 rounded text-sm h-fit ml-4 ${
            plan.status === 'active' ? 'bg-green-500/10 text-green-400' :
            plan.status === 'completed' ? 'bg-blue-500/10 text-blue-400' :
            'bg-gray-500/10 text-gray-400'
          }`}>
            {plan.status}
          </span>
        </div>

        <div className="flex items-center space-x-6 text-sm text-[#718096] mt-4">
          <span className="flex items-center space-x-1">
            <Calendar size={16} />
            <span>{plan.start_date}</span>
            <span>→</span>
            <span>{plan.end_date}</span>
          </span>

          {totalCount > 0 && (
            <span className="flex items-center space-x-2">
              <CheckCircle size={16} className={completedCount === totalCount ? 'text-green-400' : 'text-[#3b82f6]'} />
              <span className={completedCount === totalCount ? 'text-green-400' : ''}>
                {completedCount}/{totalCount} sessions ({completionRate}%)
              </span>
            </span>
          )}
        </div>
      </div>

      <h2 className="text-2xl font-bold mb-6">Training Schedule</h2>

      {weeks.length === 0 ? (
        <div className="card-base p-8 text-center text-muted">
          <p>No training weeks scheduled yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {weeks.map((week) => (
            <div key={week.id} className="card-base p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Week {week.week_number}</h3>
                <span className="text-sm text-[#718096]">
                  {week.start_date} → {week.end_date}
                </span>
              </div>

              {sessions[week.id]?.length === 0 ? (
                <p className="text-muted text-sm">No sessions scheduled</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {sessions[week.id]?.map((session) => {
                    const sessionType = session.session_type as SessionType | undefined;
                    return (
                      <div
                        key={session.id}
                        onClick={() => setCompletingSession(session)}
                        className={`flex items-center space-x-3 p-3 bg-[#0a0e14] rounded-lg cursor-pointer transition-all ${
                          session.completed ? 'opacity-60 border border-green-500/30' : 'hover:bg-[#141921]'
                        }`}
                      >
                        <div className="flex-shrink-0 relative">
                          {sessionType && getDisciplineIcon(sessionType.discipline)}
                          {session.completed && (
                            <CheckCircle
                              size={16}
                              className="absolute -top-1 -right-1 text-green-400 bg-[#0a0e14] rounded-full"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${session.completed ? 'line-through' : ''}`}>
                            {sessionType?.name || 'Unknown Session'}
                          </p>
                          <p className="text-sm text-muted flex items-center space-x-2">
                            <span>{session.scheduled_date}</span>
                            <span>•</span>
                            <span>{session.planned_duration_minutes} min</span>
                            {session.completed && session.actual_duration_minutes && session.actual_duration_minutes !== session.planned_duration_minutes && (
                              <>
                                <span>•</span>
                                <span className="flex items-center text-green-400">
                                  <Clock size={12} className="mr-1" />
                                  {session.actual_duration_minutes} min
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {completingSession && (
        <SessionCompletionModal
          session={completingSession}
          onComplete={handleCompleteSession}
          onCancel={() => setCompletingSession(null)}
        />
      )}
    </div>
  );
};

export default TrainingPlanDetail;
